import { Filter, MfsQuery } from '..';
import { ElementId } from '../fluid/snapshot';
import { MfsValue } from './mfsValue';

/**
 * System-assigned properties, that are required in all Mfs Items. These properties never change during the lifetime of
 * a single item.
 */
export type MfsSystem = {
  /** The unique id of the item. */
  readonly id: string;
};

/** Required properties that are immutable and app assigned on creation of a MfsItem */
export type MfsRequired = {
  /**
   * Data classification label.
   *
   * Best to describe the item itself rather than the source/storage
   *
   * @example
   *   Good: WordDocument, ExcelSpreedsheet, FooDocument
   *   Bad:  Sharepoint, Microsoft, Azure, OneDrive, John's Document
   */
  readonly label: string;

  /** The text created by the app for identification or description */
  readonly displayText: string;
};

/**
 * App assigned properties, that are optionally present in all Mfs items. These properties can be updated via store
 * methods by any app.
 */
export type MfsCommon = {
  /**
   * Optional Id of the required label field. This is used together with the label to ensure uniqueness across the
   * current Pod context.
   *
   * @example
   *   MfsItem with label WordDocument and labelId sharepoint.com/someDocument will no longer allow any MfsItem with that label and labelid in that Pod
   */
  readonly labelId?: string;
  /**
   * List of additional data classification labels that can be set by any application to describe alternative
   * interpretations/classifications alongside the main label.
   *
   * To create a secondary label, it is best to describe the item itself rather than the source/storage
   *
   * @example
   *   Good: WordDocument, ExcelSpreedsheet, FooDocument
   *   Bad:  Sharepoint, Microsoft, Azure, OneDrive, John's Document
   */
  readonly secondaryLabels?: string[];
};

/** The base properties common in all items. */
export type MfsBase = MfsSystem & MfsCommon & MfsRequired;

/** The Mfs Item */
export type MfsItem = MfsBase & {
  /** App properties. */
  readonly [propertyName: string]: MfsValue;
};

export type MfsSystemProperty = keyof MfsSystem;
export type MfsCommonProperty = keyof MfsCommon;
export type MfsRequiredProperty = keyof MfsRequired;

export type MfsBaseProperty = MfsSystemProperty | MfsCommonProperty | MfsCollectionCommonProperty | MfsRequiredProperty;

export type AppDataOf<T extends MfsItem> = Omit<T, MfsSystemProperty> & MfsRequired & MfsCommon;

export type AppDataOfCollection<T extends MfsCollection> = AppDataOf<T> & MfsCollectionCommon;

export type PartialItem<T extends MfsItem = MfsItem> = Partial<T> & Required<Pick<T, keyof MfsSystem>>;

/** Represents a relationship between two items. */
export type MfsRelationship = {
  /** The type of the relationship between two items. */
  type: string;

  /**
   * The kind of relationship.
   *
   * - Default is one-way (from source-> destination), can only be queried from source.
   * - Choose two-way if you need to query from both source and destination. Note that a two-way relation creates a
   *   forward link from source to destination and a reverse link from destination to source.
   */
  kind?: RelationshipKind;

  /** The direction of the relationship. Either outgoing (from source -> destination) or incoming (from destination -> source) */
  direction: 'out' | 'in';

  /** Immutable attributes to store on the edge. */
  attributes?: { [key: string]: MfsValue };
};

export enum RelationshipKind {
  /**
   * Represents a uni-directional relationship between `source` and `destination` items. This type of relationship can
   * be queried only on the direction it is originally created.
   *
   * @example
   *   // Create a one-way outgoing relationship  a: parentOf--> b
   *   const parentOf = { type: 'parentOf', direction: 'out' };
   *   mfs.addRelatedItem(a, parentOf, b);
   *
   *   // This relationship can only be queried from source item (a), in the original direction:
   *   mfs.getRelatedItems(a, parentOf}); // returns [b]
   *
   *   // It can't be queried on destination item (b), in the reverse direction:
   *   mfs.getRelatedItems(b, reverseDirectionOf(parentOf)); // no results.
   */
  OneWay,
  /**
   * Represents a bi-directional relationship between source and destination which can be queried in both original and
   * reverse directions. Choose this kind of relationship only if you need to query from both source & also destination items.
   *
   * @example
   *   // Create a two-way outgoing relationship  a: parentOf--> b
   *   const parentOf = { type: 'parentOf', direction: 'out', RelationshipKind.TwoWay };
   *   mfs.addRelatedItem(a, parentOf, b);
   *
   *   // This relationship can be queried from both source item (a), in the original direction:
   *   mfs.getRelatedItems(a, parentOf); // returns [b]
   *
   *   // And the destination item (b), in the reverse direction:
   *   mfs.getRelatedItems(b, reverseDirectionOf(parentOf)); // returns [a]
   */
  TwoWay,
}

/** Represents an MfsItem and the MfsRelationship used to get this item. */
export type MfsItemAndRelationship = {
  readonly item: MfsItem;
  readonly relationship: MfsRelationship;
};

export type MfsRelationshipFilter = Filter<MfsRelationship>;

/** Relationship query options. */
export type MfsRelationshipQuery = {
  /** The filter option */
  filter?: MfsRelationshipFilter;
};

/** Represents the position of an element in an ordered collection. */
export type SourcePosition = { place: 'at'; elementId: ElementId };

/** Represents a position in the ordered collection to move an element to. */
export type DestinationPosition =
  | {
      /**
       * Place relative to the given elementId.
       *
       * - 'before': Before the elementId, on the same level.
       * - 'after': After the elementId, on the same level.
       * - 'under': The first position under the elementId. Same as {place: 'first', parentId}
       */
      place: 'before' | 'after' | 'under';
      elementId: ElementId;
    }
  | {
      /**
       * Refers to the first and the last positions of the given collection.
       *
       * - Use `first` when you want to append an item to the collection.
       * - Use`last` when you want to prepend an item to the collection.
       */
      place: 'first' | 'last';
      /**
       * Refers to the first and last positions parented under `parentId` in the given collection.
       *
       * @example
       *   // Let collection c1 = [y, z, x:[x1, x2] ]
       *   const p1: DestinationPosition = { place: 'first' };
       *   mfs.insertItem(c1, ITEM, p1); // [ITEM, y, z, x:[x1, x2]]
       *
       *   const p2: DestinationPosition = { place: 'last' };
       *   mfs.insertItem(c1, ITEM, p2); // [y, z, x:[x1, x2], ITEM]
       *
       *   const p2: DestinationPosition = { place: 'first', parentId: x };
       *   mfs.insertItem(c1, ITEM, p3); // [y, z, x:[ITEM, x1, x2]]
       *
       *   const p4: DestinationPosition = { place: 'last', parentId: x };
       *   mfs.insertItem(c1, ITEM, p4); // [y, z, x: [x1, x2, ITEM]]
       */
      parentId?: ElementId;
    };

export type Position = SourcePosition | DestinationPosition;

/** The type of the item id property. */
export type MfsItemId = MfsItem['id'];

export type MfsItemTypeGuard<T extends MfsItem> = (item: MfsItem) => item is T;

/** The kind of collection. */
export const MfsCollectionKind = {
  //eslint-disable-next-line @typescript-eslint/naming-convention
  None: 0,
  /* This is currently the only kind of collection supported by MFS. It allows access to items by positions based on
   * item's unique element ids. Supports duplicate elements appearing in different positions.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  OrderedList: 1,
} as const;
export type MfsCollectionKind = typeof MfsCollectionKind[keyof typeof MfsCollectionKind];

/** Collection Common Types */
export type MfsCollectionCommon = { readonly kind: MfsCollectionKind };
export type MfsCollectionCommonProperty = keyof MfsCollectionCommon;
export type MfsCollection = MfsItem & MfsCollectionCommon;

/** Represents a collection element. */
export type MfsCollectionItem = {
  /** The actual {@link MfsItem} or {@link MfsCollection} element. */
  readonly item: MfsItem | MfsCollection;

  /** The immutable and globally unique identifier of the element. */
  readonly elementId: ElementId;

  /** The id of the element that this element is directly parented under, if any. Undefined for top-level items. */
  readonly parentId?: ElementId;

  /** The collection elements parented under the given element, if any. */
  readonly children?: readonly MfsCollectionItem[];
};

/** Represents collection items query options. */
export type MfsCollectionQuery = Pick<MfsQuery, 'skip' | 'top' | 'select'> & {
  /**
   * The maximum depth of child items to include in the items retrieved by the query results. You might want to use this
   * if you have an ordered & hierarchical collection. The depth is defined as distance from the collection-root, so the
   * top-level collection items are at level-1, their children at level-2 and so on. There are no elements at depth = 0.
   *
   * - Leave undefined if you need to get only level-1 items.
   * - Set depth to n >= 1 to get all items at level-1 up to level-n.
   * - Set depth to Number.MAX_SAFE_INTEGER, if you want to get all children available at all levels.
   * - Note that, in case you are querying from a sub-collection i.e you specify {@link MfsSubCollectionId} the depth will
   *   be relative to the sub-collection root specified by {@link MfsSubsCollectionId.parentId}.
   */
  depth?: number;

  /** Whether to get items in reverse order (from last to first element). Default is false. */
  reverse?: boolean;
};

export type MfsCollectionId = MfsCollection['id'];

/** Identifies the part of the collection parented under a specific element id. */
export type MfsSubCollectionId = {
  /** The unique id of the collection. */
  readonly id: MfsCollectionId;

  /** Refers to the sub-collection that is parented under the `parentId` inside the given collection. */
  readonly parentId: ElementId;
};

export type MfsCollectionEntry = Pick<MfsCollectionItem, 'elementId' | 'parentId'> & {
  itemId: MfsItemId | MfsCollectionId;
};
